import type { Deviation } from '@cdab/scania/qpr/schema'
import {
  filterTableData,
  sortTableData,
  type SortConfig
} from '@cdab/scania/qpr/utils'
import { capitalizeFirstLetter, formatISODate } from '@cdab/utils'
import {
  TdsBodyCell,
  TdsHeaderCell,
  TdsTable,
  TdsTableBody,
  TdsTableBodyRow,
  TdsTableHeader,
  TdsTableToolbar
} from '@scania/tegel-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import invariant from 'tiny-invariant'
import type { DeviationsTableProps } from './deviations-table.types'

type PreparedDeviationData = {
  auditPointNumber: string
  deviation: string | null
  proposedActions: string | null
  responsible: string | null
  expirationDate: string
  approvalDate: string
  guid: string
}

function prepareTableData(data: readonly Deviation[]): PreparedDeviationData[] {
  const preparedData = data
    .map(deviation => {
      if (deviation.deviationWithoutActions) {
        return {
          auditPointNumber: deviation.auditPointNumber,
          deviation: deviation.deviation,
          proposedActions: '(no action plan)',
          responsible: '',
          expirationDate: '',
          approvalDate: '',
          guid: deviation.clientGuid
        }
      }

      if (!deviation.draft) {
        invariant(
          deviation.expirationDate,
          'A deviation with action plan needs an expiration date'
        )
      }

      return {
        auditPointNumber: deviation.auditPointNumber,
        deviation: deviation.deviation,
        proposedActions: deviation.proposedActions,
        responsible: deviation.responsible,
        expirationDate: deviation.expirationDate
          ? formatISODate(deviation.expirationDate)
          : '',
        approvalDate: deviation.approvalDate
          ? formatISODate(deviation.approvalDate)
          : '',
        guid: deviation.clientGuid
      }
    })
    .sort((a, b) => {
      const aPoints: string[] = a.auditPointNumber.split('.')
      const bPoints: string[] = b.auditPointNumber.split('.')

      if (aPoints.length !== 2) return 1
      if (bPoints.length !== 2) return -1

      const aMajor = Number.parseInt(aPoints[0])
      const aMinor = Number.parseInt(aPoints[1])
      const bMajor = Number.parseInt(bPoints[0])
      const bMinor = Number.parseInt(bPoints[1])

      if (aMajor === bMajor) {
        if (aMinor === bMinor) return 0
        return aMinor > bMinor ? 1 : -1
      } else {
        return aMajor > bMajor ? 1 : -1
      }
    })

  return preparedData
}

export function DeviationsTable({
  deviations,
  onRowClick,
  name,
  id
}: DeviationsTableProps) {
  const { t } = useTranslation('common')
  const [sortConfig, setSortConfig] = useState<SortConfig>(null)
  const [filterQuery, setFilterQuery] = useState('')
  const [processedData, setProcessedData] = useState<PreparedDeviationData[]>(
    []
  )

  const handleTableSort = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        columnKey: string | undefined
        sortingDirection: 'asc' | 'desc'
      }>
    ) => {
      if (!event.detail.sortingDirection || !event.detail.columnKey) {
        setSortConfig(null)
        return
      }

      const { columnKey, sortingDirection } = event.detail
      setSortConfig({ columnKey, sortingDirection })
    },
    []
  )

  const handleTableFilter = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        query: string | undefined
      }>
    ) => {
      if (!event.detail.query) {
        setFilterQuery('')
        return
      }

      const { query } = event.detail
      setFilterQuery(query)
    },
    []
  )

  const customSorters = useMemo(
    () => ({
      auditPointNumber: (
        a: PreparedDeviationData,
        b: PreparedDeviationData,
        direction: 'asc' | 'desc'
      ) => {
        const aPoints = a.auditPointNumber.split('.').map(Number)
        const bPoints = b.auditPointNumber.split('.').map(Number)

        for (let i = 0; i < Math.max(aPoints.length, bPoints.length); i++) {
          const aVal = aPoints[i] || 0
          const bVal = bPoints[i] || 0
          if (aVal !== bVal) {
            return direction === 'asc' ? aVal - bVal : bVal - aVal
          }
        }
        return 0
      }
    }),
    []
  )

  useEffect(() => {
    let data = prepareTableData(deviations)

    if (filterQuery) {
      data = filterTableData(data, filterQuery)
    }

    if (sortConfig) {
      data = sortTableData(data, sortConfig, customSorters)
    }

    setProcessedData(data)
  }, [deviations, sortConfig, filterQuery, customSorters])

  return (
    <TdsTable id={id} style={{ overflow: 'auto' }} responsive>
      {name && (
        <TdsTableToolbar
          tableTitle={name}
          filter
          onTdsFilter={handleTableFilter}
        />
      )}
      <TdsTableHeader>
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='auditPointNumber'
          cellValue={capitalizeFirstLetter(t('audit-point'))}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='deviation'
          cellValue='Deviation'
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='proposedActions'
          cellValue={capitalizeFirstLetter(
            t('proposed-actions', { ns: 'audit' })
          )}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='responsible'
          cellValue={capitalizeFirstLetter(t('responsible', { ns: 'audit' }))}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='expirationDate'
          cellValue={capitalizeFirstLetter(t('expiry-date'))}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='approvalDate'
          cellValue={capitalizeFirstLetter(t('approval-date'))}
          sortable
        />
      </TdsTableHeader>
      <TdsTableBody>
        {processedData.map(row => (
          <TdsTableBodyRow
            key={row.guid}
            data-guid={row.guid}
            onClick={() => onRowClick?.(row.guid)}
          >
            <TdsBodyCell
              cellKey='auditPointNumber'
              className='tds-u-text-align-center'
            >
              {row.auditPointNumber}
            </TdsBodyCell>
            <TdsBodyCell
              cellKey='deviation'
              style={{ wordBreak: 'break-word' }}
            >
              {row.deviation}
            </TdsBodyCell>
            <TdsBodyCell cellKey='proposedActions'>
              {row.proposedActions}
            </TdsBodyCell>
            <TdsBodyCell cellKey='responsible'>{row.responsible}</TdsBodyCell>
            <TdsBodyCell cellKey='expirationDate'>
              {row.expirationDate}
            </TdsBodyCell>
            <TdsBodyCell cellKey='approvalDate'>{row.approvalDate}</TdsBodyCell>
          </TdsTableBodyRow>
        ))}
      </TdsTableBody>
    </TdsTable>
  )
}
